import React, { useRef, useState } from 'react';

import {
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    MoreHorizontal,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    className?: string;
    /** 'inline' left-aligns, adds first/last buttons and keeps "Go to page" in flow. */
    layout?: 'centered' | 'inline';
}

export function Pagination({
    currentPage,
    totalPages,
    onPageChange,
    className = '',
    layout = 'centered',
}: PaginationProps) {
    const inline = layout === 'inline';
    const [pageInput, setPageInput] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    const handlePageInputSubmit = () => {
        const trimmed = pageInput.trim();
        if (!/^\d+$/.test(trimmed)) {
            setPageInput('');
            inputRef.current?.blur();
            return;
        }
        const pageNum = Number(trimmed);
        if (pageNum >= 1 && pageNum <= totalPages) {
            onPageChange(pageNum);
        }
        setPageInput('');
        inputRef.current?.blur();
    };

    const handlePageInputKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement>,
    ) => {
        if (e.key === 'Enter') {
            handlePageInputSubmit();
        } else if (e.key === 'Escape') {
            setPageInput('');
            inputRef.current?.blur();
        }
    };

    const generatePageNumbers = () => {
        const pageNumbers = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            if (currentPage <= 2) {
                for (let i = 1; i <= 3; i++) {
                    pageNumbers.push(i);
                }
                pageNumbers.push('...');
                pageNumbers.push(totalPages);
            } else if (currentPage >= totalPages - 1) {
                pageNumbers.push(1);
                pageNumbers.push('...');
                for (let i = totalPages - 2; i <= totalPages; i++) {
                    pageNumbers.push(i);
                }
            } else {
                pageNumbers.push(1);
                pageNumbers.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pageNumbers.push(i);
                }
                pageNumbers.push('...');
                pageNumbers.push(totalPages);
            }
        }

        return pageNumbers;
    };

    return (
        <nav
            className={`relative flex items-center ${inline ? 'justify-start' : 'justify-center'} space-x-2 ${className}`}
        >
            {inline && (
                <Button
                    variant="outline"
                    size="icon"
                    onClick={() => onPageChange(1)}
                    disabled={currentPage === 1}
                    testId="Pagination__firstPage"
                >
                    <ChevronsLeft className="h-4 w-4" />
                    <span className="sr-only">First page</span>
                </Button>
            )}
            <Button
                variant="outline"
                size="icon"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                testId="Pagination__prevPage"
            >
                <ChevronLeft className="h-4 w-4" />
                <span className="sr-only">Previous page</span>
            </Button>
            {generatePageNumbers().map((page, index) => (
                <React.Fragment key={index}>
                    {page === '...' ? (
                        <Button variant="ghost" size="icon" disabled>
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">More pages</span>
                        </Button>
                    ) : (
                        <Button
                            variant={
                                currentPage === page ? 'default' : 'outline'
                            }
                            size="icon"
                            onClick={() => onPageChange(page as number)}
                        >
                            {page}
                            <span className="sr-only">Page {page}</span>
                        </Button>
                    )}
                </React.Fragment>
            ))}
            <Button
                variant="outline"
                size="icon"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                testId="Pagination__nextPage"
            >
                <ChevronRight className="h-4 w-4" />
                <span className="sr-only">Next page</span>
            </Button>
            {inline && (
                <Button
                    variant="outline"
                    size="icon"
                    onClick={() => onPageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    testId="Pagination__lastPage"
                >
                    <ChevronsRight className="h-4 w-4" />
                    <span className="sr-only">Last page</span>
                </Button>
            )}
            {totalPages > 1 && (
                <div
                    className={`flex items-center gap-1.5 ${inline ? 'pl-2' : 'absolute right-0'}`}
                    data-testid="Pagination__pageInputGroup"
                >
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                        {inline ? 'Go to page' : 'Page'}
                    </span>
                    <Input
                        ref={inputRef}
                        type="text"
                        inputMode="numeric"
                        aria-label={`Go to page, current page ${currentPage} of ${totalPages}`}
                        value={pageInput}
                        placeholder={String(currentPage)}
                        onChange={(e) => setPageInput(e.target.value)}
                        onKeyDown={handlePageInputKeyDown}
                        className="w-10 text-center font-medium"
                        data-testid="Pagination__pageInput"
                    />
                    {!inline && (
                        <span className="text-sm text-muted-foreground whitespace-nowrap">
                            of {totalPages}
                        </span>
                    )}
                </div>
            )}
        </nav>
    );
}
